import * as styles from "./TextEditor.css";
import {
  ROWS_VAR,
  content as contentStyle,
  contentWrapper,
  scrollArea,
  scrollContent,
} from "./TextEditorContent.css";

import { clsx } from "clsx";
import {
  type CSSProperties,
  type JSX,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { useControlContext } from "@frontend/contexts";
import { EditorContent, useEditor } from "@tiptap/react";

import { TextEditorContext, makeTextEditorContext } from "./TextEditor.context";
import type { TextEditorProps } from "./TextEditor.types";
import { TextEditorBubbleToolbar } from "./TextEditorBubbleToolbar";
import { TextEditorFormValue } from "./TextEditorFormValue";
import { TextEditorToolbar } from "./TextEditorToolbar";
import { DEFAULT_TEXT_EDITOR_ROWS } from "./constants";
import { createExtensions } from "./extensions";
import { readHtml } from "./tools";

export function TextEditor({
  size,
  variant,
  invalid,
  success,
  value,
  defaultValue,
  onValueChange,
  placeholder,
  label,
  tools,
  toolbarVariant,
  toolbarPlacement = "inline",
  rows,
  fill = false,
  disabled = false,
  readOnly = false,
  autoFocus = false,
  name,
  id,
  ariaLabel,
  title,
  className,
  contentClassName,
  onFocus,
  onBlur,
}: TextEditorProps): JSX.Element {
  const inherited = useControlContext();
  const resolvedSize = size ?? inherited.size;

  // Filling a container is a height the parent decides, so the rows stop
  // claiming a minimum of their own unless the caller asked for one.
  const minRows = rows ?? (fill ? 0 : DEFAULT_TEXT_EDITOR_ROWS);

  const editable = !disabled && !readOnly;

  const handlers = useRef({ onValueChange, onFocus, onBlur });

  useEffect(() => {
    handlers.current = { onValueChange, onFocus, onBlur };
  }, [onValueChange, onFocus, onBlur]);

  const extensions = useMemo(
    () => createExtensions(placeholder),
    [placeholder],
  );

  const editor = useEditor(
    {
      extensions,
      content: value ?? defaultValue ?? "",
      editable,
      autofocus: autoFocus,
      editorProps: {
        attributes: {
          class: clsx(contentStyle, contentClassName),
          role: "textbox",
          "aria-multiline": "true",
          ...(id === undefined ? {} : { id }),
          ...(ariaLabel === undefined && label === undefined
            ? {}
            : { "aria-label": ariaLabel ?? label }),
          ...(invalid === true ? { "aria-invalid": "true" } : {}),
        },
      },
      onUpdate: ({ editor }) =>
        handlers.current.onValueChange?.(readHtml(editor)),
      onFocus: () => handlers.current.onFocus?.(),
      onBlur: () => handlers.current.onBlur?.(),
    },
    [extensions],
  );

  useEffect(() => {
    editor?.setEditable(editable, false);
  }, [editor, editable]);

  useEffect(() => {
    if (editor === null || value === undefined || value === readHtml(editor)) {
      return;
    }

    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  const [toolbarHeld, setToolbarHeld] = useState(false);

  const context = useMemo(
    () =>
      makeTextEditorContext({
        editor,
        size: resolvedSize,
        disabled: !editable,
        setToolbarHeld,
      }),
    [editor, resolvedSize, editable, setToolbarHeld],
  );

  return (
    <TextEditorContext.Provider value={context}>
      <div
        data-slot="text-editor"
        title={title}
        className={clsx(
          styles.textEditor({
            size: resolvedSize,
            variant,
            invalid,
            success,
            disabled,
            fill,
          }),
          className,
        )}
        style={{ [ROWS_VAR]: minRows } as CSSProperties}
      >
        {tools !== false && "inline" === toolbarPlacement ? (
          <TextEditorToolbar
            label={label}
            tools={tools}
            variant={toolbarVariant}
          />
        ) : label === undefined ? null : (
          <TextEditorToolbar
            label={label}
            tools={[]}
            variant={toolbarVariant}
          />
        )}

        {tools !== false && "floating" === toolbarPlacement ? (
          <TextEditorBubbleToolbar
            tools={tools}
            variant={toolbarVariant}
            held={toolbarHeld}
          />
        ) : null}

        <ScrollArea className={scrollArea}>
          <ScrollAreaViewport tabIndex={-1}>
            <ScrollAreaContent className={scrollContent}>
              <EditorContent editor={editor} className={contentWrapper} />
            </ScrollAreaContent>
          </ScrollAreaViewport>

          <ScrollAreaScrollbar>
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
        </ScrollArea>

        {name === undefined ? null : <TextEditorFormValue name={name} />}
      </div>
    </TextEditorContext.Provider>
  );
}
